import { useConfigStore } from '@/stores';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SwiperRef } from 'swiper/react';

type FloorSlide = {
    index: number;
    slideContent: any[];
    tabIndex: number;
};

export const useFloorManagement = (
    floorsParam?: any[],
    amountOfFloorsToShow = 5,
    selectedFloorName?: string,
    slidesPerView: number = 5,
    selectedBuildingId?: string
) => {
    const swiperRef = useRef<SwiperRef>(null);
    const [isDisabledPrev, setIsDisabledPrev] = useState(true);
    const [isDisabledNext, setIsDisabledNext] = useState(false);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);

    const floors = useMemo(() => {
        if (!Array.isArray(floorsParam) || !floorsParam?.length) return [];
        return floorsParam;
    }, [floorsParam]);

    const floorSlides = useMemo<FloorSlide[]>(() => {
        if (!floors.length) return [];
        if (floors.length <= amountOfFloorsToShow) {
            return [
                {
                    index: 0,
                    slideContent: floors,
                    tabIndex: 0,
                },
            ];
        }
        const floorSlides: FloorSlide[] = [];
        for (let i = 0; i < floors.length; i += slidesPerView) {
            floorSlides.push({
                index: Math.floor(i / slidesPerView),
                slideContent: floors.slice(i, i + slidesPerView),
                tabIndex: i,
            });
        }
        return floorSlides;
    }, [floors, slidesPerView, amountOfFloorsToShow]);

    const selectedFloorIndex = useMemo(() => {
        if (!selectedFloorName || !floorSlides.length) return -1;
        const isMatchingFloor = (floor: {
            name?: { [key: string]: string } | Array<{ text?: string }>;
            buildingId?: string;
        }): boolean => {
            if (!floor?.name) return false;
            const isSameName = Array.isArray(floor.name)
                ? floor.name.some((item) => item?.text === selectedFloorName)
                : Object.values(floor.name).some((name) => name === selectedFloorName);
            if (!isSameName) {
                return false;
            }
            if (!selectedBuildingId || !floor.buildingId) {
                return true;
            }
            return floor.buildingId === selectedBuildingId;
        };
        for (let slideIndex = 0; slideIndex < floorSlides.length; slideIndex++) {
            const slide = floorSlides[slideIndex];
            const hasSelectedFloor = slide.slideContent.some(isMatchingFloor);
            if (hasSelectedFloor) {
                return slideIndex;
            }
        }
        return -1;
    }, [selectedFloorName, selectedBuildingId, floorSlides]);

    const shouldScroll = useMemo(() => {
        return floorSlides.length > 1;
    }, [floorSlides]);

    const handleDisableNext = useCallback((disabled: boolean) => {
        setIsDisabledNext(disabled);
    }, []);

    const handleDisablePrev = useCallback((disabled: boolean) => {
        setIsDisabledPrev(disabled);
    }, []);

    const handleScrollUp = useCallback(() => {
        if (!swiperRef?.current) return;
        swiperRef.current?.swiper?.slidePrev?.();
    }, []);

    const handleScrollDown = useCallback(() => {
        if (!swiperRef?.current) return;
        swiperRef.current?.swiper?.slideNext?.();
    }, []);

    useEffect(() => {
        if (selectedFloorIndex >= 0 && swiperRef.current?.swiper) {
            swiperRef.current.swiper.slideTo(selectedFloorIndex, 0);
            const swiper = swiperRef.current.swiper;
            setIsDisabledPrev(swiper.isBeginning);
            setIsDisabledNext(swiper.isEnd);
        }
    }, [selectedFloorIndex, screenMode]);

    return {
        floorSlides,
        isDisabledPrev,
        isDisabledNext,
        shouldScroll,
        swiperRef,
        selectedFloorIndex,
        handleScrollUp,
        handleScrollDown,
        handleDisableNext,
        handleDisablePrev,
    };
};
